import { useCallback, useEffect, useState } from 'react';
import { Moment } from 'moment';
import sessionsService from '../../services/sessions';
import useInterval from '../Util/useInterval';
import { TSession } from './Sessions';

type Params = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDescending: boolean;
  search?: string;
  pitchIds: string[];
  creationStart: Moment | null;
  creationEnd: Moment | null;
  approved: boolean | null;
};

const POLL_INTERVAL_MS = 60000;

const useSessions = (params: Params) => {
  const [data, setData] = useState<{ sessions: TSession[]; count: number } | null>(null);
  const [error, setError] = useState(false);
  const {
    page,
    pageSize,
    sortBy,
    sortDescending,
    search,
    pitchIds,
    creationStart,
    creationEnd,
    approved,
  } = params;
  const pitchKey = pitchIds.join(',');
  const startKey = creationStart?.toISOString();
  const endKey = creationEnd?.toISOString();

  const load = useCallback(async () => {
    try {
      const res = await sessionsService.getSessions({
        offset: page * pageSize,
        limit: pageSize,
        orderBy: sortBy,
        sortOrder: sortDescending ? 'Descending' : 'Ascending',
        team: search || undefined,
        pitchIds: pitchKey ? pitchKey.split(',') : undefined,
        creationStart: startKey,
        creationEnd: endKey,
        approved: approved ?? undefined,
      });
      setData({ sessions: res?.items ?? [], count: res?.total ?? 0 });
      setError(false);
    } catch (err) {
      console.error('Failed to fetch sessions', err);
      setError(true);
    }
  }, [page, pageSize, sortBy, sortDescending, search, pitchKey, startKey, endKey, approved]);

  useEffect(() => {
    load();
  }, [load]);
  useInterval(load, POLL_INTERVAL_MS);

  return { data, error, refetch: load };
};

export default useSessions;
